import React, { useState, useEffect } from "react";
import { Alert, Spinner, Modal, Form, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  FormControl,
  Select,
  Typography,
  IconButton,
} from "@mui/material";
import "../CustomStyle.css";
import CustomDataGrid from "../components/CustomDataGrid";
import {
  generateGridColumns,
  pdfGridColumns,
  pdfRequestRowMapping,
  generateGridRows,
  convertTimestampToHumanReadable,
  disableLink,
  adminPdfGridColumns,
  adminPdfOptionalColumns,
  adminPdfRequestRowMapping,
} from "../utils/PageUtils";
import "../styles/AdminActivityLogs.css";
import {
  fetchExistingCampaigns,
  getCustomizedTemplatesCreatedByUser,
  requestForPDFGeneration,
  fetchPdfGenerationRequestsForCustomer,
  updatePdfRequestStatus,
} from "../utils/api";
import PageHelmet from "../components/PageHelmet";
import RefreshIcon from "@mui/icons-material/Refresh";
import { PDF_REQUEST_STATUS } from "../constants";

function PrintContent({ userRole, bearerToken }) {
  const [error, setError] = useState(null);
  const [responseError, setResponseError] = useState(null);
  const [templateError, setTemplateError] = useState(null);

  const [campaignsData, setCampaignsData] = useState([]);
  const [existingTemplates, setExistingTemplates] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedEnvelopeTemplate, setSelectedEnvelopeTemplate] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [orderType, setOrderType] = useState("");

  const [pdfRequests, setPdfRequests] = useState([]);
  const [pdfRequestMessage, setPdfRequestMessage] = useState(null);
  const [refreshRequests, setRefreshRequests] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState(null);

  // Edit Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  // Edit modal validation states
  const [editModalMessage, setEditModalMessage] = useState(null);
  const [isEditModalError, setIsEditModalError] = useState(false);

  // Confirmation modal response states
  const [confirmModalMessage, setConfirmModalMessage] = useState(null);
  const [isConfirmModalError, setIsConfirmModalError] = useState(false);
  const [showConfirmStep, setShowConfirmStep] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);

  const dropdownStyles = {
    borderRadius: "8px",
    border: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    "&:hover": {
      backgroundColor: "#f1f1f1",
    },
    width: "100%",
  };

  useEffect(() => {
    const fetchCampaignData = async () => {
      setResponseError(null);
      setTemplateError(null);
      setIsInitialLoading(true);
      if (!bearerToken) {
        return;
      }
      const result = await fetchExistingCampaigns(bearerToken);

      if (!result.success) {
        setResponseError(result.error);
        setCampaignsData([]);
        setIsInitialLoading(false);
        return;
      }

      const campaignsWithContacts = await result.data.filter(
        (campaign) => campaign.total > 0
      );
      setCampaignsData(campaignsWithContacts);

      const templateResult = await getCustomizedTemplatesCreatedByUser(
        bearerToken,
        'active'
      );
      if (!templateResult?.success) {
        setTemplateError(
          templateResult?.error ||
          "The operation you attempted has failed. Please contact support."
        );
        setExistingTemplates([]);
        setIsInitialLoading(false);
        return;
      }
      if (templateResult.data.length === 0) {
        setTemplateError(
          "Please create a customized template first and then come back here"
        );
        setExistingTemplates([]);
        setIsInitialLoading(false);
        return;
      }
      setExistingTemplates(templateResult.data);
      setIsInitialLoading(false);
    };
    if (userRole === "customer") {
      fetchCampaignData();
    }
  }, []);

  useEffect(() => {
    const fetchExistingRequests = async () => {
      setError(null);
      setLoading(true);
      setCreditInfo(null)
      try {
        const existingRequests = await fetchPdfGenerationRequestsForCustomer(
          bearerToken
        );

        // handling 500 status code
        if (!existingRequests?.success) {
          setError(existingRequests.error);
          setPdfRequests([]);
          setLoading(false);
          return;
        }

        const credit_info = existingRequests?.data?.hasOwnProperty('credit_info');
        if (credit_info) {
          setCreditInfo(existingRequests.data.credit_info)
        }

        // handling 404 status code
        if (existingRequests.data.count === 0) {
          setPdfRequestMessage("No existing records found");
          setPdfRequests([]);
          setLoading(false);
          return;
        }

        // Sort by original timestamp before it was converted to human readable, if available
        // If request_timestamp in records is already converted, we should sort the records first.
        const sortedRecords = existingRequests.data.records.toSorted((a, b) => new Date(b.request_timestamp) - new Date(a.request_timestamp));
        const finalDataset = sortedRecords.map((record) => ({
          ...record,
          request_timestamp: convertTimestampToHumanReadable(
            record.request_timestamp
          ),
          status_updated: convertTimestampToHumanReadable(
            record?.status_updated
          ),
          conditional_url: disableLink(record),
        }));
        setPdfRequests(finalDataset);

        setPdfRequestMessage(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    if (refreshRequests) {
      fetchExistingRequests();
      setRefreshRequests(false);
    }
  }, [refreshRequests]);

  // Auto-lock order type to print_and_email for postcard templates
  useEffect(() => {
    setSelectedEnvelopeTemplate(null);
    if (selectedTemplate?.type === "Postcard") {
      setOrderType("Print & Mail");
    } else if (selectedTemplate?.type === "Envelope") {
      setOrderType("Download PDF");
    }
    else {
      setOrderType("");
    }
  }, [selectedTemplate]);

  const sendRequestForPdfGeneration = async (campaign, template, orderType) => {
    setError(null);
    setMessage(null);

    if (!campaign) {
      setError(
        "Please select campaign to generate print collaterals against them"
      );
      return;
    }
    if (!template) {
      setError(
        "Please select a template to generate print collaterals against them"
      );
      return;
    }
    if (!orderType) {
      setError("Please select Delivery Method");
      return;
    }
    if (selectedTemplate?.type === "Letter" && orderType === "Print & Mail" && !selectedEnvelopeTemplate) {
      setError("Please select an envelope template for your letter.");
      return;
    }

    setLoading(true);

    const finalTemplate =
      selectedTemplate?.type === "Letter" && orderType === "Print & Mail"
        ? `${template},${selectedEnvelopeTemplate.template_name}`
        : template;

    const result = await requestForPDFGeneration(
      campaign,
      finalTemplate,
      orderType,
      bearerToken
    );

    if (result.success) {
      setMessage(result.message);
      setRefreshRequests(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleEditClick = (request) => {
    setSelectedRequest(request);
    setEditStatus("");
    setEditModalMessage(null);
    setIsEditModalError(false);

    setConfirmModalMessage(null);
    setIsConfirmModalError(false);
    setShowConfirmStep(false);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = () => {
    setEditModalMessage(null);
    setIsEditModalError(false);

    if (!selectedRequest || !editStatus) {
      setIsEditModalError(true);
      setEditModalMessage("Please select a Status");
      return;
    };

    if (selectedRequest.request_status === editStatus) {
      setIsEditModalError(true);
      setEditModalMessage("No change in Status. Select a different Status to update.");
      return;
    }

    setShowConfirmStep(true);
  };
  const confirmStatusUpdate = async () => {
    if (!selectedRequest) return;

    setEditLoading(true);
    setConfirmModalMessage(null);
    setIsConfirmModalError(false);

    const result = await updatePdfRequestStatus(
      bearerToken,
      selectedRequest.unique_id,
      selectedRequest.customer_id,
      editStatus
    );

    setEditLoading(false);

    setConfirmModalMessage(result.message);
    setIsConfirmModalError(!result.success);

    if (result.success) {
      setRefreshRequests(true);
    }
  };

  const adminPdfColumns = generateGridColumns(adminPdfGridColumns, "request_status", undefined, handleEditClick, adminPdfOptionalColumns);
  const adminPdfRows = generateGridRows(pdfRequests, adminPdfRequestRowMapping);

  const pdfRequestsRow = generateGridRows(pdfRequests, pdfRequestRowMapping);
  const pdfRequestColumns = generateGridColumns(pdfGridColumns, 'campaign', undefined, undefined, adminPdfOptionalColumns);

  const currentColumns = userRole === "admin" ? adminPdfColumns : pdfRequestColumns;
  const currentRows = userRole === "admin" ? adminPdfRows : pdfRequestsRow;

  if (isInitialLoading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  function formatMessage(msg) {
    return msg.split('\n').map((line, ind) => (
      <span key={ind}>
        {line}
        <br />
      </span>
    ));
  }

  const handleRowClick = (e) => {
    setSelectedRowId((prevId) =>
      prevId === e.id ? null : e.id
    );
  };

  if (userRole === "admin") {
    return (
      <div>
        <PageHelmet
          title="Print Requests"
          description="View all print collateral requests."
          url={`${process.env.REACT_APP_REDIRECT_URI}/print_collaterals`}
        />
        <div className="user-container activity-logs-container">
          <div className="requests-header-container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h1 className="user-heading" style={{ margin: 0 }}>Print Collateral Requests</h1>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="refresh-tooltip">
                  <span className="tooltip-text">Refresh Grid</span>
                </Tooltip>
              }
            >
              <IconButton
                onClick={() => setRefreshRequests(true)}
                aria-label="Refresh grid"
                className="refresh-icon-button"
                style={{
                  width: "36px",
                  height: "36px",
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  color: "#2f539b",
                  padding: 0,
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </OverlayTrigger>
          </div>
          {pdfRequestMessage && <Alert variant="info" className="text-center m-5">{pdfRequestMessage}</Alert>}
          {loading && (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" >
                <span className="visually-hidden">Please wait...</span>
              </Spinner>
            </div>
          )}
          {!pdfRequestMessage && !loading && (
            <div className="print-requests-container">
              <CustomDataGrid
                rows={currentRows}
                columns={currentColumns}
                totalRows={pdfRequests.length}
                onRowClick={handleRowClick}
                selectedRowId={selectedRowId}
              />
            </div>
          )}
        </div>

        {/* Edit & Confirmation Status Modal */}
        <Modal
          show={isEditModalOpen}
          onHide={() => setIsEditModalOpen(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{!showConfirmStep ? "Edit Request Status" : "Confirm Status Update"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {!showConfirmStep ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Status :</Form.Label>
                  <Form.Select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    <option value="" disabled>Select a Status</option>
                    {PDF_REQUEST_STATUS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {editModalMessage && (
                  <Alert
                    variant={isEditModalError ? "danger" : "success"}
                    className="text-center"
                  >
                    {editModalMessage}
                  </Alert>
                )}
              </>
            ) : (
              <>
                <p style={{ marginBottom: "1rem" }}>
                  Are you sure you want to update the request status to{" "}
                  <strong>{editStatus}</strong>?
                </p>

                {confirmModalMessage && (
                  <Alert variant={isConfirmModalError ? "danger" : "success"}>
                    {confirmModalMessage}
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>

          <Modal.Footer style={{ justifyContent: showConfirmStep ? "flex-end" : "center" }}>
            {!showConfirmStep ? (
              <Button
                variant="primary"
                onClick={handleEditSubmit}
                style={{ backgroundColor: "#2f539b", borderColor: "#2f539b" }}
              >
                Update
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirmStep(false)}
                  disabled={editLoading || (confirmModalMessage && !isConfirmModalError)}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmStatusUpdate}
                  disabled={editLoading || (confirmModalMessage && !isConfirmModalError)}
                  style={{ backgroundColor: "#2f539b", borderColor: "#2f539b" }}
                >
                  {editLoading ? <Spinner size="sm" animation="border" /> : "Confirm"}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </div >
    );
  }

  return userRole === "customer" ? (
    <div className="print-content-wrapper">
      <PageHelmet
        title="Generate Print Collaterals"
        description="Generate pdfs with TraQR."
        url={`${process.env.REACT_APP_REDIRECT_URI}/print_collaterals`}
      />
      <div
        className={`print-container  ${responseError || templateError ? "no-background" : ""
          }`}
      >
        <h1 className="main-heading">Order Print Collaterals</h1>

        {responseError || templateError ? (
          <div className="create-print-msg">
            {responseError && <Alert variant="danger">{responseError}</Alert>}
            {templateError && <Alert variant="danger">{templateError}</Alert>}
          </div>
        ) : (
          <div className="print-content-container">
            <FormControl
              className="print-content-form"
              style={{ marginTop: "20px", marginBottom: "2%" }}
            >
              <Typography
                variant="body2"
                style={{
                  color: campaignsData.length === 0 ? "gray" : "black",
                }}
                className="custom-label"
              >
                Choose a campaign <span style={{ color: "red" }}>*</span>
              </Typography>
              <div className="campaign-select-container">
                <Select
                  native
                  defaultValue="select_campaign"
                  style={dropdownStyles}
                  disabled={campaignsData.length === 0}
                  onChange={(e) =>
                    setSelectedCampaign(
                      campaignsData?.find(
                        (c) => c.campaign_name === e.target.value
                      )
                    )
                  }
                >
                  <option value="select_campaign">
                    Select a campaign
                  </option>
                  {campaignsData?.map((campaign, index) => (
                    <option
                      key={index}
                      value={campaign.campaign_name}
                    >
                      {campaign.campaign_name} ({campaign.destination_url}) - {campaign.total} contacts
                    </option>
                  ))}
                </Select>
              </div>
            </FormControl>
            <FormControl className="print-content-form">
              <Typography
                variant="body2"
                className="custom-label"
              >
                Choose your proofed template <span style={{ color: "red" }}>*</span>
              </Typography>
              <div className="campaign-select-container">
                <Select
                  native
                  defaultValue="template"
                  style={dropdownStyles}
                  onChange={(e) =>
                    setSelectedTemplate(
                      existingTemplates.find(
                        (t) => t.template_name === e.target.value
                      )
                    )
                  }
                >
                  <option value="template">
                    Select a template
                  </option>
                  {Object.entries(
                    existingTemplates?.reduce((acc, template) => {
                      const owner = template.owner || "Other";
                      if (!acc[owner]) {
                        acc[owner] = [];
                      }
                      acc[owner].push(template);
                      return acc;
                    }, {}) || {}
                  ).map(([owner, templates]) => (
                    <optgroup key={owner} label={owner}>
                      {templates.map((template) => (
                        <option
                          key={template.template_name}
                          value={template.template_name}
                        >
                          [{template.choice}] - {template.template_name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </div>

              {/* Order Type */}
              <div style={{ marginTop: "18px", marginBottom: "6px" }}>
                <Typography
                  variant="body2"
                  style={{
                    width: "fit-content",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px"
                  }}
                  className="custom-label"
                >
                  Choose delivery method <span style={{ color: "red" }}>*</span>
                </Typography>
                <Typography
                  variant="caption"
                  className="delivery-info-text"
                >
                  Only one delivery mode is allowed per campaign & template.
                </Typography>
                <div className="delivery-options-container">
                  <label
                    className="delivery-option-label"
                    style={{
                      cursor: selectedTemplate?.type === "Envelope" ? "not-allowed" : "pointer",
                      opacity: selectedTemplate?.type === "Envelope" ? 0.45 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name="orderType"
                      value="Print & Mail"
                      checked={orderType === "Print & Mail"}
                      onChange={(e) => {
                        setOrderType(e.target.value);
                        setSelectedEnvelopeTemplate(null);
                      }}
                      style={{ accentColor: "#2f539b", width: "16px", height: "16px" }}
                      disabled={selectedTemplate?.type === "Envelope"}
                    />{" "}
                    Print &amp; Mail - Full Service
                  </label>
                  <label
                    className="delivery-option-label"
                    style={{
                      cursor: selectedTemplate?.type === "Postcard" ? "not-allowed" : "pointer",
                      opacity: selectedTemplate?.type === "Postcard" ? 0.45 : 1,
                    }}
                  >
                    <input
                      type="radio"
                      name="orderType"
                      value="Download PDF"
                      checked={orderType === "Download PDF"}
                      onChange={(e) => {
                        setOrderType(e.target.value);
                        setSelectedEnvelopeTemplate(null);
                      }}
                      disabled={selectedTemplate?.type === "Postcard"}
                      style={{ accentColor: "#2f539b", width: "16px", height: "16px" }}
                    />{" "}
                    Download (DIY)
                  </label>
                </div>
              </div>

              {selectedTemplate?.type === "Letter" && orderType === "Print & Mail" && (
                <div style={{ marginTop: "18px" }}>
                  <Typography
                    variant="body2"
                    className="custom-label"
                  >
                    Choose your envelope template {" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <div className="campaign-select-container">
                    <Select
                      native
                      defaultValue="envelope_template"
                      style={dropdownStyles}
                      onChange={(e) =>
                        setSelectedEnvelopeTemplate(
                          existingTemplates.find(
                            (t) => t.template_name === e.target.value
                          )
                        )
                      }
                    >
                      <option value="envelope_template">
                        Select an envelope template
                      </option>
                      {existingTemplates
                        ?.filter((t) => t.type === "Envelope")
                        .map((template) => (
                          <option
                            key={template.template_name}
                            value={template.template_name}
                          >
                            [{template.choice}] - {template.template_name}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>
              )}

              <div style={{ textAlign: "center" }}>
                <Button
                  variant="dark"
                  className="responsive-button"
                  disabled={
                    !selectedCampaign?.campaign_name ||
                    !selectedTemplate?.template_name ||
                    !orderType ||
                    (selectedTemplate?.type === "Letter" && orderType === "Print & Mail" && !selectedEnvelopeTemplate)
                  }
                  onClick={() => {
                    sendRequestForPdfGeneration(
                      selectedCampaign.campaign_name,
                      selectedTemplate.template_name,
                      orderType
                    );
                  }}
                >
                  {orderType == "Download PDF" ? "Generate" : "Order"}
                </Button>
              </div>
            </FormControl>
          </div>
        )}
      </div>
      {!responseError && !templateError && (
        <>
          {error && (
            <div className="create-print-msgs" style={{ whiteSpace: "pre-line" }}>
              <Alert variant="danger">{error}</Alert>
            </div>
          )}

          {message && (
            <div className="create-print-msgs" data-testid="print-info-msg">
              <Alert variant="success">{formatMessage(message)}</Alert>
            </div>
          )}

          {loading ? (
            <div className="loading-container">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <div className="existing-requests-container">
              <div className="requests-header-container">
                <h2 className="requests-heading">Print Collateral Requests</h2>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="refresh-tooltip">
                      <span className="tooltip-text">Refresh Grid</span>
                    </Tooltip>
                  }
                >
                  <IconButton
                    onClick={() => setRefreshRequests(true)}
                    aria-label="Refresh grid"
                    className="refresh-icon-button"
                    style={{
                      width: "36px",
                      height: "36px",
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      color: "#2f539b",
                      padding: 0,
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </OverlayTrigger>

              </div>

              {creditInfo &&
                <p>
                  <strong>Credit limit:</strong> {creditInfo.credit_limit}, <strong>Credit used:</strong> {creditInfo.credit_used}
                  {Number(creditInfo.extra_credit_limit) !== 0 && (
                    <>
                      , <strong>Extra credit limit:</strong> {creditInfo.extra_credit_limit}, <strong>Extra credit used:</strong> {creditInfo.extra_credit_used}
                    </>
                  )}
                </p>
              }

              <div className="requests-grid">
                <CustomDataGrid
                  rows={currentRows}
                  columns={currentColumns}
                  totalRows={pdfRequests.length}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowId}
                />
              </div>

            </div>
          )}
        </>
      )}
    </div>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
}

export default PrintContent;